import React, { useState, useEffect, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Swal from 'sweetalert2';
import { TelegramBotService, TelegramMessageData } from './api/telegramBot';
import "./assets/App.css";

interface LocationData {
  city: string;
  region: string;
  country: string;
}

const AcessoTg: FC = () => {
  const navigate = useNavigate();
  const [nome, setNome] = useState<string>("");
  const [ipAddress, setIpAddress] = useState<string>("Não disponível");
  const [location, setLocation] = useState<LocationData>({ 
    city: "", 
    region: "", 
    country: "" 
  });
  const [isLocationConsentGiven, setIsLocationConsentGiven] = useState<boolean>(false);
  const [hasFullName, setHasFullName] = useState<boolean>(false);
  const [showNameError, setShowNameError] = useState<boolean>(false);
  
  const pageUrl = process.env.REACT_APP_PAGE_URL || "";
  const redirectedLink = process.env.REACT_APP_LINK_REDIRECTED || "";
  
  const telegramService = TelegramBotService.getInstance();
  
  const handleLocationConsent = (): void => {
    setIsLocationConsentGiven(true);
  };

  useEffect(() => {
    if (isLocationConsentGiven) {
      const fetchIpAndLocation = async (): Promise<void> => {
        try {
          const ipResponse = await fetch("https://api.ipify.org/?format=json");
          if (ipResponse.ok) {
            const ipData = await ipResponse.json();
            setIpAddress(ipData.ip);
            console.log("IP Capturado:", ipData.ip);

            const locationResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
            if (locationResponse.ok) {
              const locationData = await locationResponse.json();
              setLocation({
                city: locationData.city || "Desconhecido",
                region: locationData.region || "Desconhecido",
                country: locationData.country_name || "Desconhecido",
              });
              console.log("Localização capturada:", locationData);
            }
          }
        } catch (error) {
          console.error("Erro ao capturar IP ou localização:", error);
        }
      };

      fetchIpAndLocation();
    }
  }, [isLocationConsentGiven]);

  const validateName = (name: string): { isValid: boolean; message: string } => {
    const trimmed = name.trim();
    const parts = trimmed.split(/\s+/).filter(part => part.length > 0);
    
    if (parts.length < 2) {
      return { isValid: false, message: 'Por favor, digite seu nome completo' };
    }
    
    if (/([a-zA-ZÀ-ÿ])\1{3,}/i.test(trimmed)) {
      return { isValid: false, message: 'Padrão de letras repetidas detectado' };
    }
    
    for (const part of parts) {
      if (part.length < 2) {
        return { isValid: false, message: 'Cada parte do nome deve ter pelo menos 2 letras' };
      }
      
      if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(part)) {
        return { isValid: false, message: 'O nome contém caracteres inválidos' };
      }
      
      if (!/[aeiouáéíóúâêîôûãõàèìòùäëïöü]/i.test(part)) {
        return { isValid: false, message: 'O nome parece conter palavras sem vogais' };
      }
      
      if (/([a-zA-ZÀ-ÿ])\1{2,}/.test(part)) {
        return { isValid: false, message: 'O nome contém repetições inválidas' };
      }
      
      if (/[0-9]/.test(part)) {
        return { isValid: false, message: 'Números não são permitidos no nome' };
      }
      
      if (/[^a-zA-ZÀ-ÿ\s'-]/.test(part)) {
        return { isValid: false, message: 'Caracteres especiais não são permitidos' };
      }
    } 
    
    if (trimmed.length < 6 || trimmed.length > 60) {
      return { isValid: false, message: 'O nome deve ter entre 6 e 60 caracteres' };
    }
    
    const uniqueLetters = new Set(trimmed.toLowerCase().replace(/[^a-záéíóúâêîôûãõàèìòùäëïöüç]/g, ''));
    if (uniqueLetters.size < 3) {
      return { isValid: false, message: 'O nome parece muito curto ou repetitivo' };
    }
    
    return { isValid: true, message: '' };
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNome(value);
    const validation = validateName(value);
    const isValid = validation.isValid;
    setHasFullName(isValid);
    setShowNameError(value.trim() !== '' && !isValid);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const validation = validateName(nome);
    if (!validation.isValid) {
      setShowNameError(true);
      Swal.fire({
        icon: 'warning',
        title: 'Nome inválido',
        text: validation.message,
        confirmButtonColor: '#00a884',
      });
      return;
    }

    if (!telegramService.isConfigured()) {
      console.error('ERRO: Configuração do Telegram Bot não está definida no arquivo .env');
      Swal.fire({
        icon: 'error',
        title: 'Erro de configuração',
        text: 'O serviço de mensagens não está configurado corretamente.',
        confirmButtonColor: '#00a884',
      });
      return;
    }

    if (!isLocationConsentGiven) {
      const result = await Swal.fire({
        icon: 'info',
        title: 'Informações pessoais',
        text: 'Este site coleta suas informações para melhorar a experiência. Você concorda com o uso dessas informações?',
        confirmButtonText: 'Sim, concordo',
        cancelButtonText: 'Cancelar',
        showCancelButton: true,
        confirmButtonColor: '#00a884',
        cancelButtonColor: '#6c757d',
      });

      if (!result.isConfirmed) {
        return;
      }

      handleLocationConsent();
    }

    try {
      const telegramData: TelegramMessageData = {
        nome,
        ipAddress,
        city: location.city,
        region: location.region,
        country: location.country,
        link: pageUrl,
        redirecionar: redirectedLink,
        lang: "pt-BR",
      };

      await telegramService.sendMessage(telegramData);
      console.log('Dados enviados com sucesso para o Telegram');

      setNome('');

      const result = await Swal.fire({
        icon: "success" as const,
        title: "Ei...",
        text: "Isso é um aviso! Entendeu?",
        confirmButtonText: "Sim, entendi!",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCancelButton: false,
        showCloseButton: false,
      });

      if (result.isConfirmed) {
        navigate('/api/tgLink', { 
          state: { 
            fromForm: true, 
            name: nome 
          } 
        });
      }
    } catch (error) {
      console.error("Erro ao enviar dados para Formspree:", error);
      Swal.fire({
        icon: 'error',
        title: 'Erro no envio',
        text: 'Não foi possível enviar suas informações. Tente novamente mais tarde.',
        confirmButtonColor: '#00a884',
      });
    }
  };

  return (
    <div>
      <Helmet>
        <title>𝗖ɪɴᴇ𝗗ᴇᴀғ's ‧ 𝐋𝐓𝐃𝐀™ - Acesso Exclusivo</title>
        <meta name="description" content="🍿 Preencha seu nome e entre no grupo exclusivo do Telegram." />
      </Helmet>
      {!isLocationConsentGiven && (
        <div className="location-consent-banner">
          <p>Este site coleta suas informações para melhorar a experiência. Ao continuar, você concorda com o uso dessas informações pessoais?</p>
          <button onClick={handleLocationConsent} className="btBaixo">Sim, concordo</button>
        </div>
      )}

      <form id="formulario" onSubmit={handleSubmit}>
        <div className="form-banner-topo"></div>

        <div className="form-group">
          <label htmlFor="nomeAcesso">Nome e sobrenome</label>
          <div className="input-with-icon">
            <input
              id="nomeAcesso"
              name="nome"
              required
              type="text"
              value={nome}
              onChange={handleNameChange}
              className={`${hasFullName ? 'has-icon' : ''} ${showNameError ? 'input-error' : ''}`}
              placeholder=""
            />
            {hasFullName ? (
              <svg className="verification-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#00a884" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : showNameError ? (
              <svg className="error-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#ff4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : null}
          </div>
          {showNameError && <span className="error-message">Por favor, insira seu nome e sobrenome</span>}
        </div>
        <input type="hidden" name="link" value={pageUrl} />
        <input type="hidden" name="redirecionar" value={redirectedLink} />
        <input type="hidden" name="lang" value="pt-BR" />

        <div className="attention-box">
          <div className="attention-header">
            <svg className="attention-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V14M12 17H12.01M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3 className="attention-title">Atenção</h3>
          </div>
          <div className="attention-content">
            <p>📌 <b>Importante:</b> Esse é um grupo ativo de filmes e séries pra comunidade surda.</p>
            <p>✅ Faça seus pedidos no grupo CinePedidos ou site.</p>
            <div className="attention-divider"></div>
            <p>✅ A equipe baixará os filmes e séries solicitados.</p>
            <p>✅ Seu pedido chegou? Aproveite pra assistir!</p>
          </div>
        </div>
        {hasFullName && isLocationConsentGiven && (
          <button 
            type="submit" 
            className="txtButton"
            style={{
              backgroundColor: '#00a884'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#017561';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#00a884';
            }}
          >
            Acessar
          </button>
        )}
      </form>
    </div>
  );
};

export default AcessoTg;
